<template>
  <VApp>
    <!-- Loading cue for step-to-step navigation. The previous step stays on screen while
         the next one loads (its setup awaits its data), so a thin bar replaces the global
         white overlay, which CustomLoading skips inside the join flow. AB#64268 -->
    <NuxtLoadingIndicator color="rgb(var(--v-theme-primary))" :height="3" aria-hidden="true" data-testid="join-loading-bar" />
    <a href="#main-content" class="skip-nav" @click="cleanHash">{{ t('nav.btn.skipToMainContent') }}</a>
    <JoinHeader />
    <!-- While a step change holds the previous step on screen, it is inert: its controls
         take no focus and are not read out under the cover. AB#64268 -->
    <VMain id="main-content" tabindex="-1" class="ida-content" :inert="stepChanging" :aria-busy="stepChanging || undefined">
      <!-- The step being left stays on screen until the next one has its data; this dims it
           with a spinner for exactly that wait. Driven by the navigation itself, not by a
           store flag the next step may never set. AB#64268
           SLoadingOverlay announces the wait, inert main or not. AB#64981 -->
      <SLoadingOverlay :loading="stepChanging" scrim data-testid="join-step-cover" />
      <slot><NuxtPage /></slot>
    </VMain>
  </VApp>
</template>

<script lang="ts" setup>
import JoinHeader from '~/components/layout/join/JoinHeader.vue'
import { useJoinStore } from '~/stores/join'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import { focusIfLost, isClientArrival } from '~/utils/focus'
import {
  JOIN_SELECT_OFFER, JOIN_TEAM_LEADER, JOIN_AGREEMENTS, JOIN_MY_DETAILS, JOIN_VAT, JOIN_PAYMENT, JOIN_CONFIRMATION,
} from '~/utils/joinRoutes'

const joinStore = useJoinStore()
const { t } = useI18n()
const { cleanHash } = useSkipNav()
const nuxtApp = useNuxtApp()
const router = useRouter()

// True from the start of a step change until the next step has rendered: Nuxt raises
// page:loading:start in its router guard and page:finish when the new page's Suspense
// resolves. Not page:loading:end: Nuxt also raises that for a failed navigation, and a
// repeated Next is a duplicated-navigation failure while the first one is still loading.
// Subscribed on mount, like CustomLoading, so a hard load never flashes it. AB#64268
const stepChanging = useJoinStepChanging()
const unsubscribers: Array<() => void> = []

// Entering the flow from a page on another layout (Create Account on /join), the step
// change starts before this layout mounts, so the cover never goes up and the step-change
// branch below never runs: the focused control unmounted with the old page and focus
// stayed on the body. On that first arrival, land on the main content once, on mount and
// again when the first step has rendered, but only if nothing has taken focus. A hard load
// is no arrival: it keeps the skip link as the first Tab stop. AB#65432
let firstArrivalPending = isClientArrival()
const focusMainIfLost = () => focusIfLost(() => document.getElementById('main-content'), { preventScroll: true })

onMounted(() => {
  if (firstArrivalPending) focusMainIfLost()
  unsubscribers.push(
    nuxtApp.hook('page:loading:start', () => { stepChanging.value = true }),
    nuxtApp.hook('page:finish', () => {
      const arriving = firstArrivalPending
      firstArrivalPending = false
      if (stepChanging.value) {
        stepChanging.value = false
        // Focus was on the held step, which went inert; land keyboard and screen reader
        // users on the new step's content instead of the document body. AB#64268
        nextTick(() => document.getElementById('main-content')?.focus({ preventScroll: true }))
      } else if (arriving) {
        focusMainIfLost()
      }
    }),
    // A page that throws while suspending never reaches page:finish.
    nuxtApp.hook('vue:error', () => { stepChanging.value = false }),
    // An aborted navigation renders nothing new. A duplicated or superseded one leaves a
    // navigation still loading, so those keep the cover up.
    router.afterEach((_to, _from, failure) => {
      if (isNavigationFailure(failure, NavigationFailureType.aborted)) stepChanging.value = false
    }),
    // Query-only navigations stay on the same step, so there is nothing to wait for.
    // Registered after Nuxt's own guard, so this runs after page:loading:start.
    router.beforeEach((to, from) => {
      if (to.path === from.path) stepChanging.value = false
    }),
  )
})
onBeforeUnmount(() => {
  unsubscribers.forEach(unsubscribe => unsubscribe())
  // Shared state: leaving the flow mid-change must not leave it set for the next entry.
  stepChanging.value = false
})

// Rebuild the join session from the backend (/join) once, here in the parent
// layout, instead of in every step page's onMounted — so the internal fetches
// share a single loading span and run only once per entry into the flow, since
// the layout persists across step navigation. Runs under SSR (useAsyncData): the
// store is populated before the page renders and lands in the payload (no client
// refetch). hydrateSession() re-enters the Nuxt context across its awaits so the
// post-/join fetches work server-side; it no-ops when the store is already
// populated. Step pages read the hydrated store and enforce step order.
// The handler returns a truthy sentinel (not the record, which we never read):
// useAsyncData duplicates the request on the client if the handler resolves
// falsy, and hydrateSession() returns undefined on a cold load with no recruit.
// (PR #221 — AB#60662)
await useAsyncData('ida-session', async () => {
  await joinStore.hydrateSession()
  return true
})

// Fetch every step's page chunk once the flow is on screen, so moving to the next step
// waits only on its data, not on downloading its code. The chunks are small and the
// layout persists across steps, so this runs once per entry into the flow. Paths are
// localized: sub-locale markets (/fr, /de) route every step under the language prefix,
// and the bare path matches nothing there. AB#64268
const stepPaths = [JOIN_SELECT_OFFER, JOIN_TEAM_LEADER, JOIN_AGREEMENTS, JOIN_MY_DETAILS, JOIN_VAT, JOIN_PAYMENT, JOIN_CONFIRMATION]
  .map(path => useLocalizedTo(path).localizedTo)
onMounted(() => {
  for (const path of stepPaths) {
    if (path.value) preloadRouteComponents(path.value)
  }
})
</script>

<style lang="scss" scoped>
.ida-content {
  background-color: #FFFFFF;
  flex: 1;
  padding: 0;
  // Containing block for the step-change cover (and the steps' own loading covers), so
  // they dim the page area and leave the join header visible.
  position: relative;
}
</style>
